import React from "react";
import { Link } from "react-router-dom";
import { Star, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/api";

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const discount =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;

  return (
    <div className="group flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-[#0b3d75]/30 transition-all duration-200">
      <Link to={`/product/${product.slug}`} className="relative aspect-square bg-slate-50 overflow-hidden">
        <img
          src={product.images?.[0] || "https://via.placeholder.com/400"}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        {discount > 0 && (
          <Badge className="absolute top-2 left-2 bg-amber-500 hover:bg-amber-500 text-white font-bold">
            -{discount}%
          </Badge>
        )}
        {product.featured && (
          <Badge className="absolute top-2 right-2 bg-[#0b3d75] hover:bg-[#0b3d75] text-white">
            Featured
          </Badge>
        )}
      </Link>

      <div className="p-4 flex flex-col flex-1">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
          {product.brand}
        </div>
        <Link
          to={`/product/${product.slug}`}
          className="mt-1 font-semibold text-slate-900 line-clamp-2 min-h-[2.75rem] hover:text-[#0b3d75] transition-colors"
        >
          {product.name}
        </Link>

        <div className="flex items-center gap-1 mt-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star
              key={i}
              className={`w-3.5 h-3.5 ${i <= Math.round(product.rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
            />
          ))}
          <span className="text-xs text-slate-500 ml-1">({product.review_count || 0})</span>
        </div>

        <div className="mt-3 flex items-end gap-2">
          <div className="text-xl font-black text-[#0b3d75]">{formatMoney(product.price)}</div>
          {product.compare_at_price && product.compare_at_price > product.price && (
            <div className="text-sm text-slate-400 line-through mb-0.5">
              {formatMoney(product.compare_at_price)}
            </div>
          )}
        </div>
        <div className="text-xs text-slate-500">per {product.unit || "each"}</div>

        <Button
          onClick={() => addItem(product)}
          className="mt-3 w-full bg-[#0b3d75] hover:bg-[#0a4e94] text-white font-semibold"
        >
          <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;
